import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Search, Star, Clock3 } from 'lucide-react';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import { createVipVisitor, listMyVipVisitors } from '../../services/staffVisitorService';
import '../../styles/StaffVipVisitorsPage.css';

function getArrivalStatus(expectedAt, entryAt) {
  if (entryAt) return 'Arrivé';
  if (!expectedAt) return 'Sans horaire';
  return new Date(expectedAt) < new Date() ? 'En retard' : 'À venir';
}

export default function StaffVipVisitorsPage() {
  const [vipVisitors, setVipVisitors] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm();

  const fetchVipVisitors = useCallback(async () => {
    setLoading(true);

    try {
      const res = await listMyVipVisitors({ search });
      if (res.success) {
        setVipVisitors(res.data || []);
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Erreur lors du chargement des visiteurs VIP');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchVipVisitors();
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [fetchVipVisitors]);

  const onSubmit = async (data) => {
    setError('');
    setSuccess('');

    try {
      const payload = {
        ...data,
        expectedAt: new Date(data.expectedAt).toISOString(),
      };

      const res = await createVipVisitor(payload);
      if (res.success) {
        setSuccess('Visiteur VIP ajouté avec succès');
        reset();
        fetchVipVisitors();
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Erreur lors de la création du visiteur VIP');
    }
  };

  return (
    <div className="vip-visitors-page">
      <div className="staff-vip-layout">
        <form onSubmit={handleSubmit(onSubmit)} className="staff-vip-form-card">
          <div className="staff-vip-form-header">
            <h2>Ajouter un visiteur VIP</h2>
            <p>Le personnel peut enregistrer une arrivée VIP planifiée avec sa date de venue.</p>
          </div>

          {error && <div className="error-banner">{error}</div>}
          {success && <div className="success-banner">{success}</div>}

            <div className="staff-vip-form-grid">
              <div className="form-group">
                <label>Nom complet *</label>
                <input {...register('fullName', { required: 'Le nom est requis' })} placeholder="Nom du visiteur" />
                {errors.fullName && <span className="field-error">{errors.fullName.message}</span>}
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input type="email" {...register('email', { required: 'L\'email est requis' })} placeholder="vip@email.com" />
                {errors.email && <span className="field-error">{errors.email.message}</span>}
              </div>

              <div className="form-group">
                <label>Téléphone *</label>
                <input type="tel" {...register('phone', { required: 'Le téléphone est requis' })} placeholder="+216 XX XXX XXX" />
                {errors.phone && <span className="field-error">{errors.phone.message}</span>}
              </div>

              <div className="form-group">
                <label>Quand viendra-t-il ? *</label>
                <input type="datetime-local" {...register('expectedAt', { required: 'La date et l\'heure sont requises' })} />
                {errors.expectedAt && <span className="field-error">{errors.expectedAt.message}</span>}
              </div>
            </div>

            <div className="form-group">
              <label>Remarques</label>
              <textarea {...register('comments')} rows={4} placeholder="Informations utiles pour l'accueil" />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Enregistrement...' : 'Ajouter le VIP'}
              </button>
            </div>
          </form>

          <section className="staff-vip-list-card">
            <div className="staff-vip-list-header">
              <div>
                <h2>Mes visiteurs VIP</h2>
                <p>Seulement les visiteurs ajoutés par ce compte.</p>
              </div>

              <label className="staff-vip-search">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </label>
            </div>

            {loading ? (
              <LoadingSkeleton lines={7} />
            ) : vipVisitors.length === 0 ? (
              <div className="staff-vip-empty">Aucun visiteur VIP enregistré pour ce compte.</div>
            ) : (
              <div className="staff-vip-list">
                {vipVisitors.map((item) => (
                  <article key={item.visit_id} className="staff-vip-item">
                    <div className="staff-vip-item-top">
                      <span className="staff-vip-pill"><Star size={14} fill="currentColor" /> VIP</span>
                      <span className={`staff-vip-status status-${getArrivalStatus(item.expected_at, item.entry_at).toLowerCase().replace(/\s+/g, '-')}`}>
                        {getArrivalStatus(item.expected_at, item.entry_at)}
                      </span>
                    </div>
                    <h3>{item.full_name}</h3>
                    <p>{item.email}</p>
                    <p>{item.phone}</p>
                    <div className="staff-vip-time">
                      <Clock3 size={15} />
                      <span>{item.expected_at ? new Date(item.expected_at).toLocaleString('fr-FR') : 'Horaire non défini'}</span>
                    </div>
                    {item.comments && <p className="staff-vip-comments">{item.comments}</p>}
                  </article>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
  );
}